import { useEffect } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useCreateSupplier, useUpdateSupplier } from '@/hooks/useSuppliers';
import type { CreateSupplierRequest, Supplier } from '@/services/supplierService';

const schema = z.object({
  name: z.string().trim().min(1, 'Enter a name').max(255, 'Use at most 255 characters'),
  code: z.string().trim().min(1, 'Enter a code').max(50, 'Use at most 50 characters'),
  adjustedName: z.string().trim().max(255, 'Use at most 255 characters'),
  businessUnitKey: z.string().trim().max(100, 'Use at most 100 characters'),
  businessUnitName: z.string().trim().max(255, 'Use at most 255 characters'),
  contactEmail: z
    .string()
    .trim()
    .max(255, 'Use at most 255 characters')
    .refine((v) => v === '' || z.string().email().safeParse(v).success, 'Enter a valid email address'),
});

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = {
  name: '',
  code: '',
  adjustedName: '',
  businessUnitKey: '',
  businessUnitName: '',
  contactEmail: '',
};

const toValues = (s: Supplier): FormValues => ({
  name: s.name ?? '',
  code: s.code ?? '',
  adjustedName: s.adjustedName ?? '',
  businessUnitKey: s.businessUnitKey ?? '',
  businessUnitName: s.businessUnitName ?? '',
  contactEmail: s.contactEmail ?? '',
});

interface SupplierDialogProps {
  open: boolean;
  /** The supplier being edited; omit to create a new one */
  supplier?: Supplier | null;
  onOpenChange: (open: boolean) => void;
}

export function SupplierDialog({ open, supplier, onOpenChange }: SupplierDialogProps) {
  const isEdit = !!supplier;
  const createSupplier = useCreateSupplier();
  const updateSupplier = useUpdateSupplier();
  const isPending = createSupplier.isPending || updateSupplier.isPending;

  const form = useForm<FormValues>({ resolver: zodResolver(schema) as Resolver<FormValues>, defaultValues: EMPTY });

  // Reset whenever the dialog opens, so a previous edit never leaks into the next one
  useEffect(() => {
    if (open) form.reset(supplier ? toValues(supplier) : EMPTY);
  }, [open, supplier, form]);

  const onSubmit = (values: FormValues) => {
    const payload = {
      ...values,
      contactEmail: values.contactEmail || undefined,
    } as CreateSupplierRequest;
    const onSuccess = () => onOpenChange(false);
    // A duplicate code is the one conflict the user can fix right here
    const onError = (error: unknown) => {
      const response = (error as { response?: { status?: number; data?: { detail?: string } } }).response;
      if (response?.status === 409 && /code/i.test(response.data?.detail ?? '')) {
        form.setError('code', { message: 'This code is already in use' });
        form.setFocus('code');
      }
    };

    if (supplier) {
      updateSupplier.mutate({ id: supplier.id, data: payload }, { onSuccess, onError });
    } else {
      createSupplier.mutate(payload, { onSuccess, onError });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !isPending && onOpenChange(next)}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 bg-card p-0 sm:max-w-lg">
        <DialogHeader className="px-6 pb-2 pt-6 text-left">
          <DialogTitle>{isEdit ? 'Edit supplier' : 'Add supplier'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Changes apply to the supplier list shown when creating contracts.'
              : 'It becomes available in the supplier dropdown when creating contracts.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" autoFocus {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid gap-5 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Code</FormLabel>
                      <FormControl>
                        <Input autoComplete="off" {...field} />
                      </FormControl>
                      <FormDescription>Must be unique.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="adjustedName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Adjusted name <span className="font-normal text-muted-foreground">(optional)</span>
                      </FormLabel>
                      <FormControl>
                        <Input autoComplete="off" {...field} />
                      </FormControl>
                      <FormDescription>Shown in the contract dropdown.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="businessUnitName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Business unit <span className="font-normal text-muted-foreground">(optional)</span>
                      </FormLabel>
                      <FormControl>
                        <Input autoComplete="off" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="businessUnitKey"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Business unit key <span className="font-normal text-muted-foreground">(optional)</span>
                      </FormLabel>
                      <FormControl>
                        <Input autoComplete="off" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="contactEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Contact email <span className="font-normal text-muted-foreground">(optional)</span>
                    </FormLabel>
                    <FormControl>
                      <Input type="email" autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="gap-2 border-t px-6 py-4 sm:space-x-0">
              <Button type="button" variant="ghost" disabled={isPending} onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {isEdit ? 'Save changes' : 'Add supplier'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
